import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from 'react'

import { theme } from '@hhgtech/hhg-components/misc'
import throttle from 'lodash/throttle'
import { useIntl } from 'react-intl'
import Slider from 'react-slick'

import { Container } from 'components/atoms/container'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import {
  LOCALE,
  WeekOfPregnancy as WeekOfPregnancyType,
} from 'interfaces/types'
import { genPropTrack, TRACK_GA } from 'utils/tracking'

import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { NextArrow, PreviousArrow } from './arrow'
import { WeekDetail } from './detail'
import {
  StyledContentContainer,
  StyledIcon as Icon,
  StyledIconContainer,
  StyledPillContainer,
  StyledScrollContainer,
  StyledStickyTopbar,
  StyledWeekOfPregnancy,
  StyledWeekOfPregnancyContainer,
} from './index.styled'
import { Trimeter } from './trimeter'

import { AppContext } from 'state/appContext'

import { Text } from '@hhgtech/hhg-components/mantine'

type PillProps = {
  iconSrc?: string | null
  title: string
  className?: string
  onSelect?: () => void
}

type Props = {
  data: WeekOfPregnancyType[]
  onSelectWeek?: (week: number) => void
  className?: string
  defaultWeekIndex?: number
  withStickyScroll?: {
    scrollYFrom: number
  }
  disabledRecommendation?: boolean
  isArticle?: boolean
}

const LONG_TITLE_LOCALE = [LOCALE.Myanmar, LOCALE.Taiwan]
const Pill = ({ iconSrc, title, className, onSelect }: PillProps) => {
  const currentUrl = useCurrentUrl()
  const isSelected = useMemo(() => className?.includes('selected-week-pill'), [
    className,
  ])

  const { locale } = useRouterExtend()

  return (
    <StyledPillContainer
      {...genPropTrack(TRACK_GA.WEEK_PREGNANCY, '', {
        key: 'label',
        value: currentUrl,
      })}
      onClick={() => {
        onSelect?.()
      }}
      className={
        className
          ? 'week-pill'.concat(' non-pointer-event-child ', className)
          : 'week-pill non-pointer-event-child'
      }
    >
      <StyledIconContainer
        size={64}
        iconSize={40}
        backgroundColor={theme.colors.white}
      >
        <img
          loading="lazy"
          src={iconSrc || `${assetImagePath}/week_default.png`}
          alt=""
        />
      </StyledIconContainer>

      {isSelected ? (
        <Text
          weight="semiBold"
          size="n1"
          color={theme.colors.gray800}
          {...(LONG_TITLE_LOCALE.includes(locale) && {
            style: { fontSize: '10px' },
          })}
        >
          {title}
        </Text>
      ) : (
        <Text
          size="n1"
          color={theme.colors.gray600}
          {...(LONG_TITLE_LOCALE.includes(locale) && {
            style: { fontSize: '10px' },
          })}
        >
          {title}
        </Text>
      )}
    </StyledPillContainer>
  )
}

const WeekOfPregnancy = ({
  isArticle = false,
  data,
  onSelectWeek,
  className,
  defaultWeekIndex,
  withStickyScroll,
  disabledRecommendation = false,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const [selectedWeek, setSelectedWeek] = useState<number>(
    defaultWeekIndex || 0,
  )
  const isDefaultWeekSameAsCurrentWeek = useMemo(
    () =>
      Boolean(
        isArticle &&
          'undefined' !== typeof defaultWeekIndex &&
          defaultWeekIndex === selectedWeek,
      ),
    [defaultWeekIndex, selectedWeek, isArticle],
  )

  const currentWeekDetail: WeekOfPregnancyType = data[selectedWeek]
  const [readingProgress, setReadingProgress] = useState<number>(0)
  const [isStickyTopbarShown, setIsStickyTopbarShown] = useState(false)
  const [isStickyScrollShown, setIsStickyScrollShown] = useState(false)
  const getWeekNearby = (offset: number) => {
    const NUMBER_OF_WEEK = data.length
    let result = selectedWeek + offset
    if (result >= NUMBER_OF_WEEK) {
      result -= NUMBER_OF_WEEK
    } else if (result < 0) {
      result += NUMBER_OF_WEEK
    }

    return result
  }

  const generateClassName = (index: number) => {
    const showWeeks = isMobile ? [-1, 1] : [-3, -2, -1, 1, 2, 3]

    const hideWeeks = isMobile ? [-4, -3, -2, 2, 3, 4] : [-4, 4]
    return index === selectedWeek
      ? 'selected-week-pill'
      : showWeeks.map((offset) => getWeekNearby(offset)).includes(index)
      ? undefined
      : hideWeeks.map((offset) => getWeekNearby(offset)).includes(index)
      ? 'opacity-50'
      : 'opacity-10'
  }

  const handleSelectWeek = (index: number) => {
    setSelectedWeek(index)
    if (onSelectWeek) onSelectWeek(index)
  }

  const windowScrollY = useRef<number>(0)

  useEffect(() => {
    const html = document.querySelector('html')
    if (withStickyScroll) {
      const handleScroll = throttle(() => {
        if (window.scrollY > windowScrollY.current) {
          if (window.scrollY >= withStickyScroll.scrollYFrom) {
            setIsStickyTopbarShown(true)
          }
        } else {
          setIsStickyTopbarShown(false)
          setIsStickyScrollShown(false)
        }

        windowScrollY.current = window.scrollY
        setReadingProgress(
          window.scrollY /
            ((html?.scrollHeight ?? 0) - (html?.clientHeight ?? 0) || 1),
        )
      }, 200)

      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <div className={className}>
      {isLoggedIn && <Trimeter currentWeek={selectedWeek} />}
      <StyledWeekOfPregnancyContainer>
        <Container>
          <StyledContentContainer className="main-content">
            <StyledWeekOfPregnancy>
              {data.length ? (
                <StyledScrollContainer
                  data-is-sticky={withStickyScroll && isStickyTopbarShown}
                  data-visible={isStickyScrollShown}
                >
                  <Slider
                    infinite
                    swipeToSlide
                    slidesToShow={11}
                    slidesToScroll={1}
                    focusOnSelect
                    speed={300}
                    centerMode
                    beforeChange={(_, i) => handleSelectWeek(i)}
                    initialSlide={defaultWeekIndex || 0}
                    arrows={!isMobile}
                    nextArrow={<NextArrow />}
                    prevArrow={<PreviousArrow />}
                  >
                    {data.map((w, i) => (
                      <div key={i} className="centered-pill-wrapper">
                        <Pill
                          className={generateClassName(i)}
                          title={w?.name}
                          iconSrc={w?.fruitIcon}
                        />
                      </div>
                    ))}
                  </Slider>
                </StyledScrollContainer>
              ) : undefined}

              {currentWeekDetail && (
                <WeekDetail
                  currentWeekDetail={currentWeekDetail}
                  disabledRecommendation={
                    disabledRecommendation || isDefaultWeekSameAsCurrentWeek
                  }
                />
              )}
            </StyledWeekOfPregnancy>
          </StyledContentContainer>
        </Container>
      </StyledWeekOfPregnancyContainer>

      {withStickyScroll && (
        <>
          <StyledStickyTopbar
            data-visible={isStickyTopbarShown}
            style={
              {
                '--reading-progress': readingProgress,
              } as CSSProperties
            }
          >
            {isMobile ? (
              <div
                className="mobile-container"
                onClick={() => setIsStickyScrollShown((s) => !s)}
              >
                <StyledIconContainer
                  iconSize={24}
                  size={32}
                  backgroundColor={theme.colors.white}
                  className="fruit-icon"
                >
                  <img
                    loading="lazy"
                    src={
                      currentWeekDetail?.fruitIcon || '/icons/week_carrot.svg'
                    }
                    alt=""
                  />
                </StyledIconContainer>
                <Text
                  size="p3"
                  color={theme.colors.white}
                  className="mobile-text"
                >
                  {currentWeekDetail?.name}
                </Text>
                <Icon
                  className="mobile-arrow"
                  color={theme.colors.white}
                  iconSrc="/icons/chevron.svg"
                  size={18}
                  data-reverse={isStickyScrollShown}
                />
              </div>
            ) : (
              <Container className="container">
                <img
                  loading="lazy"
                  className="mb-logo"
                  src="/images/mb-logo.svg"
                  alt=""
                  onClick={() =>
                    (window.location.href = window.location.origin)
                  }
                />
                <div
                  className="clickable-area"
                  onClick={() => setIsStickyScrollShown((s) => !s)}
                >
                  <Text className="text" size="label1">
                    {f({ id: 'weekOfPregnancy.title' })}:{' '}
                    {currentWeekDetail?.name}
                  </Text>
                  <img
                    loading="lazy"
                    className="arrow"
                    data-reverse={isStickyScrollShown}
                    src={'/svg/arrow.svg'}
                    alt=""
                  />
                </div>
              </Container>
            )}
          </StyledStickyTopbar>
        </>
      )}
    </div>
  )
}

export { WeekOfPregnancy }
